import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import { forceClearCachesAndReload } from './sw/pwaUtils'

/**
 * Cross-Tab Broadcast Utility
 *
 * Enables communication across all browser tabs of the same origin.
 * Uses BroadcastChannel API with localStorage fallback for older browsers.
 *
 * Primary use cases:
 * - SESSION_REFRESH: Hard refresh all tabs when admin triggers session refresh
 * - FILTER_REFRESH: Hard refresh all tabs when admin triggers filter refresh
 * - LOGOUT: Hard refresh all tabs when admin triggers logout
 *
 * Hard refresh is delegated to pwaUtils.forceClearCachesAndReload() which:
 * - Unregisters service workers
 * - Clears all caches
 * - Reloads with cache-busting URL parameter
 */

// Channel name for session refresh broadcasts
const BROADCAST_CHANNEL_NAME = 'loop_session_broadcast'

// localStorage key for fallback mechanism
const STORAGE_KEY = 'loop_broadcast_message'

// Message types - each represents a specific admin action
export const BROADCAST_TYPES = {
  SESSION_REFRESH: 'SESSION_REFRESH',
  FILTER_REFRESH: 'FILTER_REFRESH',
  LOGOUT: 'LOGOUT'
} as const

export type BroadcastType = (typeof BROADCAST_TYPES)[keyof typeof BROADCAST_TYPES]

export interface BroadcastMessage {
  type: BroadcastType
  timestamp: number
  reason?: string
  userId?: string
  tabId?: string
}

// Generate unique tab ID
const TAB_ID = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

// Track if we've already processed a message to avoid duplicates
const processedMessages = new Set<string>()

// BroadcastChannel instance (if supported)
let broadcastChannel: BroadcastChannel | null = null

// Message handler callback
let messageHandler: ((message: BroadcastMessage) => void) | null = null

/**
 * Check if BroadcastChannel API is supported
 */
export function isBroadcastChannelSupported(): boolean {
  return typeof BroadcastChannel !== 'undefined'
}

/**
 * Generate unique message ID for deduplication
 */
function getMessageId(message: BroadcastMessage): string {
  return `${message.type}-${message.timestamp}-${message.tabId || 'unknown'}`
}

/**
 * Handle incoming broadcast message
 */
function handleMessage(message: BroadcastMessage): void {
  // Skip messages from this tab
  if (message.tabId === TAB_ID) {
    return
  }

  // Deduplicate messages (important for localStorage fallback which can fire multiple times)
  const messageId = getMessageId(message)
  if (processedMessages.has(messageId)) {
    return
  }
  processedMessages.add(messageId)

  // Clean up old message IDs to keep the set size at a maximum of 100.
  while (processedMessages.size > 100) {
    const oldestId = processedMessages.values().next().value
    processedMessages.delete(oldestId)
  }

  // Call the registered handler
  if (messageHandler) {
    messageHandler(message)
  }
}

/**
 * localStorage event handler (fallback for browsers without BroadcastChannel)
 */
function handleStorageEvent(event: StorageEvent): void {
  if (event.key !== STORAGE_KEY || !event.newValue) {
    return
  }

  try {
    const message = JSON.parse(event.newValue) as BroadcastMessage
    handleMessage(message)
  } catch (error) {
    console.error('[CrossTabBroadcast] Failed to parse storage event:', error)
  }
}

/**
 * Initialize the cross-tab broadcast listener
 * Call this early in app initialization
 */
export function initCrossTabBroadcast(onMessage: (message: BroadcastMessage) => void): () => void {
  messageHandler = onMessage

  if (isBroadcastChannelSupported()) {
    // Use BroadcastChannel API (preferred)
    try {
      broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
      broadcastChannel.onmessage = (event) => {
        handleMessage(event.data as BroadcastMessage)
      }
    } catch (error) {
      console.error('[CrossTabBroadcast] Failed to create BroadcastChannel:', error)
      // Fall back to localStorage
      window.addEventListener('storage', handleStorageEvent)
    }
  } else {
    // Fallback: Use localStorage storage event
    window.addEventListener('storage', handleStorageEvent)
  }

  // Return cleanup function
  return () => {
    messageHandler = null
    if (broadcastChannel) {
      broadcastChannel.close()
      broadcastChannel = null
    }
    window.removeEventListener('storage', handleStorageEvent)
  }
}

/**
 * Broadcast a message to all other tabs
 */
export function broadcast(type: BroadcastType, reason?: string, userId?: string): void {
  const message: BroadcastMessage = {
    type,
    timestamp: Date.now(),
    reason,
    userId,
    tabId: TAB_ID
  }

  // Track in analytics
  posthog.capture('cross_tab_broadcast_sent', {
    type,
    reason,
    userId,
    tabId: TAB_ID
  })

  Sentry.addBreadcrumb({
    category: 'cross-tab',
    message: `Broadcasting ${type}`,
    data: { reason, userId },
    level: 'info'
  })

  if (isBroadcastChannelSupported() && broadcastChannel) {
    // Use BroadcastChannel API
    try {
      broadcastChannel.postMessage(message)
    } catch (error) {
      console.error('[CrossTabBroadcast] Failed to post message:', error)
      // Fall back to localStorage
      broadcastViaStorage(message)
    }
  } else {
    // Fallback: Use localStorage
    broadcastViaStorage(message)
  }
}

/**
 * Broadcast via localStorage (fallback mechanism)
 * The storage event only fires in OTHER tabs, not the current one
 */
function broadcastViaStorage(message: BroadcastMessage): void {
  try {
    // Set the value to trigger storage event in other tabs
    localStorage.setItem(STORAGE_KEY, JSON.stringify(message))
    // Clean up immediately (the event has already been dispatched)
    setTimeout(() => {
      localStorage.removeItem(STORAGE_KEY)
    }, 100)
  } catch (error) {
    console.error('[CrossTabBroadcast] localStorage fallback failed:', error)
  }
}

/**
 * Broadcast to all tabs and optionally hard-reload this tab.
 * Shared implementation for session refresh, filter refresh, and logout.
 */
function broadcastAndReload(type: BroadcastType, reason: string, userId?: string, reload = true): void {
  broadcast(type, reason, userId)

  posthog.capture(`${type.toLowerCase()}_all_tabs_initiated`, {
    reason,
    userId,
    tabId: TAB_ID,
    isInitiator: true
  })

  Sentry.captureMessage(`${type} all tabs initiated: ${reason}`, 'info')

  if (reload) {
    // Short delay to ensure broadcast is sent before this tab reloads
    setTimeout(() => {
      forceClearCachesAndReload('cross-tab-broadcast')
    }, 100)
  }
}

/** Force hard reload all tabs — use when admin triggers session refresh */
export function forceReloadAllTabs(reason: string, userId?: string): void {
  broadcastAndReload(BROADCAST_TYPES.SESSION_REFRESH, reason, userId)
}

/** Force hard reload all tabs — use when admin triggers filter refresh */
export function forceReloadAllTabsForFilter(reason: string, userId?: string): void {
  broadcastAndReload(BROADCAST_TYPES.FILTER_REFRESH, reason, userId)
}

/** Broadcast logout to all tabs (no reload — caller handles logout flow) */
export function logoutAllTabs(reason: string, userId?: string): void {
  broadcastAndReload(BROADCAST_TYPES.LOGOUT, reason, userId, false)
}

/**
 * Default handler for broadcast messages received from OTHER tabs.
 * All message types trigger a hard refresh to ensure fresh application state.
 */
export function handleForceReloadMessage(message: BroadcastMessage): void {
  posthog.capture('broadcast_received', {
    reason: message.reason,
    userId: message.userId,
    tabId: TAB_ID,
    originTabId: message.tabId,
    isInitiator: false,
    broadcastType: message.type
  })

  Sentry.captureMessage(`Broadcast ${message.type} received from another tab: ${message.reason}`, 'info')

  setTimeout(() => {
    forceClearCachesAndReload('cross-tab-broadcast')
  }, 50)
}
